import type { SchemeMechanic, ArchetypeKey } from '@infoscend-claims/shared';

export interface ArchetypeDefinition {
  key: ArchetypeKey;
  lucideIcon: string;
  schemeTypes: string[];
  template: SchemeMechanic;
}

const now = new Date();
const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
const quarter = `Q${Math.ceil((now.getMonth() + 1) / 3)}_${now.getFullYear()}`;

const BASE_PAYOUT = {
  currency: 'IDR' as const,
  taxTreatment: 'NET_DEDUCT' as const,
  taxWithholdingPercent: 0.02,
  pphCategory: 'PPH_23_SERVICE' as const,
};

const BASE_CONSTRAINTS = {
  stackability: { rule: 'FULLY_STACKABLE' as const },
  externalVerifications: [],
  targetEntities: {},
};

export const ARCHETYPE_DEFINITIONS: ArchetypeDefinition[] = [
  {
    key: 'BINARY_PASS_FAIL',
    lucideIcon: 'CheckSquare',
    schemeTypes: ['VISIBILITY'],
    template: {
      baselineUnit: 'OUTLET',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DISPLAY_EVIDENCE', evidenceType: 'PHOTO' },
          { type: 'MIN_FACINGS', count: 4 },
          { type: 'SKU_PRESENCE', targetSkus: [] },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'FIXED', amount: 500000 },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.OREO.${quarter}.VISIBILITY`, budgetCap: null },
    },
  },
  {
    key: 'LINEAR_VOLUME_REBATE',
    lucideIcon: 'TrendingUp',
    schemeTypes: ['VOLUME_REBATE'],
    template: {
      baselineUnit: 'VOLUME_CASE',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DOCUMENT_MATCH', docType: 'INVOICE' },
          { type: 'SKU_PRESENCE', targetSkus: [] },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'LINEAR', perUnitAmount: 15000 },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.BISKUAT.${quarter}.VOLUME_REBATE`, budgetCap: null },
    },
  },
  {
    key: 'TIERED_TARGET',
    lucideIcon: 'BarChart3',
    schemeTypes: ['VOLUME_REBATE'],
    template: {
      baselineUnit: 'SALES_VALUE',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'QUARTERLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [{ type: 'DOCUMENT_MATCH', docType: 'INVOICE' }],
      },
      payout: {
        ...BASE_PAYOUT,
        type: 'TIERED',
        evaluationMetric: 'VALUE',
        tierLogic: 'HIGHEST_QUALIFIED',
        tiers: [
          { minThreshold: 0, maxThreshold: 500000000, rewardAmount: 1.5, rewardType: 'PERCENTAGE' },
          { minThreshold: 500000001, maxThreshold: 999999999, rewardAmount: 2.5, rewardType: 'PERCENTAGE' },
          { minThreshold: 1000000000, maxThreshold: null, rewardAmount: 3.5, rewardType: 'PERCENTAGE' },
        ],
      },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.CADBURY.${quarter}.VOLUME_REBATE`, budgetCap: { amount: 500000000, currency: 'IDR' } },
    },
  },
  {
    key: 'SCAN_BACK_SUBSIDY',
    lucideIcon: 'ScanLine',
    schemeTypes: ['PROMO_DISCOUNT_SUBSIDY'],
    template: {
      baselineUnit: 'INVOICE',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DOCUMENT_MATCH', docType: 'INVOICE' },
          { type: 'SKU_PRESENCE', targetSkus: [] },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'LINEAR', perUnitAmount: 0 },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.OREO.${quarter}.PROMO`, budgetCap: null },
    },
  },
  {
    key: 'NPD_PLACEMENT',
    lucideIcon: 'PackagePlus',
    schemeTypes: ['NPD_LISTING'],
    template: {
      baselineUnit: 'SKU_OUTLET',
      measurementWindow: { type: 'POINT_IN_TIME', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'ONE_TIME' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DOCUMENT_MATCH', docType: 'INVOICE' },
          { type: 'LISTING_WEEKS', minWeeks: 4 },
          { type: 'SKU_PRESENCE', targetSkus: [] },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'FIXED', amount: 250000 },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.TOBLERONE.${quarter}.NPD`, budgetCap: null },
    },
  },
  {
    key: 'EXPENSE_MATCHING',
    lucideIcon: 'Receipt',
    schemeTypes: ['COOP_MARKETING', 'SAMPLING_ACTIVITY'],
    template: {
      baselineUnit: 'OUTLET',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DOCUMENT_MATCH', docType: 'INVOICE' },
        ],
      },
      payout: {
        ...BASE_PAYOUT,
        type: 'REIMBURSEMENT',
        reimbursementPercent: 50,
        maxPerBaseline: 3500000,
        requiresVendorInvoice: true,
      },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.OREO.${quarter}.COOP`, budgetCap: null },
    },
  },
  {
    key: 'NEGATIVE_DEDUCTION',
    lucideIcon: 'Minus',
    schemeTypes: ['OUTLET_RETURN'],
    template: {
      baselineUnit: 'OUTLET',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DOCUMENT_MATCH', docType: 'RECEIPT' },
          { type: 'SKU_PRESENCE', targetSkus: [] },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'LINEAR', perUnitAmount: -5000 },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.BISKUAT.${quarter}.RETURNS`, budgetCap: null },
    },
  },
  {
    key: 'KPI_BOUNTY',
    lucideIcon: 'Trophy',
    schemeTypes: ['SALES_TEAM_INCENTIVE'],
    template: {
      baselineUnit: 'SALES_REP',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'CUSTOM_PREDICATE', description: 'Sales rep achieved ≥ N productive calls in period' },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'FIXED', amount: 1000000 },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.OREO.${quarter}.INCENTIVE`, budgetCap: null },
    },
  },
  {
    key: 'SHELF_RENTAL',
    lucideIcon: 'Store',
    schemeTypes: ['VISIBILITY'],
    template: {
      baselineUnit: 'OUTLET',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'RETAILER_SIGNATURE_PRESENT' },
          { type: 'DISPLAY_EVIDENCE', evidenceType: 'PHOTO' },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'SHELF_RENTAL', monthlyRate: 500000, spaceType: 'TOP_SHELF', monopolyClause: false },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.OREO.${quarter}.SHELF`, budgetCap: null },
    },
  },
  {
    key: 'FREE_GOODS_RATIO',
    lucideIcon: 'Gift',
    schemeTypes: ['PROMO_DISCOUNT_SUBSIDY'],
    template: {
      baselineUnit: 'VOLUME_CASE',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DOCUMENT_MATCH', docType: 'INVOICE' },
          { type: 'SKU_PRESENCE', targetSkus: [] },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'FREE_GOODS_RATIO', freeQty: 2, orderedQty: 20, sku: '', skuUnitCost: 15000, antiResaleClause: true },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.BISKUAT.${quarter}.FREE_GOODS`, budgetCap: null },
    },
  },
  {
    key: 'TRANSACTION_INSTANT_DISCOUNT',
    lucideIcon: 'Zap',
    schemeTypes: ['PROMO_DISCOUNT_SUBSIDY'],
    template: {
      baselineUnit: 'INVOICE',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DOCUMENT_MATCH', docType: 'INVOICE' },
          { type: 'SKU_PRESENCE', targetSkus: [] },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'TRANSACTION_INSTANT_DISCOUNT', discountType: 'PERCENT', amount: 12, triggerCondition: '3+ cartons any eligible SKU', maxDurationDays: 14 },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.CADBURY.${quarter}.PROMO`, budgetCap: null },
    },
  },
  {
    key: 'LOYALTY_POINT_ACCRUAL',
    lucideIcon: 'Award',
    schemeTypes: ['SALES_TEAM_INCENTIVE'],
    template: {
      baselineUnit: 'SALES_VALUE',
      measurementWindow: { type: 'ROLLING', startDate: null, endDate: null, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'DOCUMENT_MATCH', docType: 'INVOICE' },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'LOYALTY_POINT_ACCRUAL', pointsPerCurrencyUnit: 1, currencyUnit: 10000, pointValueCurrency: 1, validityMonths: 6 },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.BISKUAT.${quarter}.LOYALTY`, budgetCap: null },
    },
  },
  {
    key: 'PAYMENT_TERMS_EXTENSION',
    lucideIcon: 'Clock',
    schemeTypes: ['TRADE_SPEND_POOL'],
    template: {
      baselineUnit: 'INVOICE',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'VOLUME_THRESHOLD', minVolume: 100, uom: 'CASES' },
          { type: 'DOCUMENT_MATCH', docType: 'INVOICE' },
        ],
      },
      payout: { ...BASE_PAYOUT, type: 'PAYMENT_TERMS_EXTENSION', baseTermDays: 30, extensionDays: 60, qualifyingCondition: 'Min. 100 cases per delivery' },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.CADBURY.${quarter}.PAYMENT_TERMS`, budgetCap: null },
    },
  },
  {
    key: 'WEIGHTED_KPI_BOUNTY',
    lucideIcon: 'BarChart2',
    schemeTypes: ['SALES_TEAM_INCENTIVE'],
    template: {
      baselineUnit: 'SALES_REP',
      measurementWindow: { type: 'FIXED_PERIOD', startDate: firstOfMonth, endDate: lastOfMonth, frequency: 'MONTHLY' },
      eligibility: {
        logicalOperator: 'AND',
        conditions: [
          { type: 'CUSTOM_PREDICATE', description: 'Sales rep submitted monthly performance data' },
        ],
      },
      payout: {
        ...BASE_PAYOUT,
        type: 'WEIGHTED_KPI_BOUNTY',
        basePayout: 500000,
        metrics: [
          { name: 'Sales Volume', weighting: 0.5, measurementType: 'PERCENT_OF_TARGET' as const },
          { name: 'Effective Calls', weighting: 0.3, measurementType: 'PERCENT_OF_TARGET' as const },
          { name: 'Must-Win SKU', weighting: 0.2, measurementType: 'PERCENT_OF_TARGET' as const },
        ],
        gatekeeper: { metricName: 'Sales Volume', minimumThresholdPercent: 85 },
        multiplierCurve: [
          { achievementPercentMin: 0, achievementPercentMax: 85, multiplier: 0 },
          { achievementPercentMin: 85, achievementPercentMax: 100, multiplier: 'LINEAR' as const },
          { achievementPercentMin: 100, achievementPercentMax: 110, multiplier: 1.0 },
          { achievementPercentMin: 110, achievementPercentMax: null, multiplier: 1.3 },
        ],
      },
      constraints: BASE_CONSTRAINTS,
      fundAllocation: { glAccountCode: `TRADE_SPEND.OREO.${quarter}.DSR_INCENTIVE`, budgetCap: null },
    },
  },
];

export const ARCHETYPE_BY_KEY: Record<ArchetypeKey, ArchetypeDefinition> =
  Object.fromEntries(ARCHETYPE_DEFINITIONS.map((a) => [a.key, a])) as Record<ArchetypeKey, ArchetypeDefinition>;
